import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { TrashIcon } from '@phosphor-icons/react';

import { deleteTeam, updateTeam } from '@/api-models/teams';
import { useAuth } from '@/contexts/auth-context';
import { teamPath, useSpace } from '@/contexts/space-context';
import { SettingsSection } from '@/features/settings/components/SettingsSection';
import { Button } from '@/ui/button/Button';
import { Input } from '@/ui/Input';

/** Settings → Team → General: rename the active team, or delete it (owners only). */
export function TeamGeneralSection() {
  const navigate = useNavigate();
  const { me } = useAuth();
  const { teamId, teams, mutateTeams } = useSpace();
  const team = teams.find((t) => t.id === teamId);

  const [name, setName] = useState(team?.name ?? '');
  const [isSavingName, setIsSavingName] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  if (!team) {
    return <p className="text-xs text-fg-muted">This team no longer exists, or you no longer have access to it.</p>;
  }

  async function handleSaveName() {
    const trimmed = name.trim();
    if (!trimmed || isSavingName) return;
    setIsSavingName(true);
    try {
      await updateTeam(teamId, { name: trimmed });
      mutateTeams();
      toast.success('Team renamed');
    } catch {
      toast.error('Could not rename the team');
    } finally {
      setIsSavingName(false);
    }
  }

  async function handleDeleteTeam() {
    setIsDeleting(true);
    try {
      await deleteTeam(teamId);
      mutateTeams();
      navigate(teamPath(me.personalTeamId));
      toast.success('Team deleted');
    } catch {
      toast.error('Could not delete the team');
    } finally {
      setIsDeleting(false);
    }
  }

  return (
    <>
      <SettingsSection title="Team name" description="Shown wherever this team appears across the app.">
        <div className="flex gap-2">
          <Input type="text" value={name} onChange={(e) => setName(e.target.value)} className="flex-1" />
          <Button
            type="button"
            variant="primary"
            size={9}
            onClick={handleSaveName}
            disabled={!name.trim() || name.trim() === team.name}
            isLoading={isSavingName}
          >
            Save
          </Button>
        </div>
      </SettingsSection>

      {team.role === 'owner' && !team.is_personal && (
        <SettingsSection
          title="Danger zone"
          description="Deleting a team is permanent. Its projects, workspaces, and member list are removed for everyone."
        >
          {!showDeleteConfirm ? (
            <div>
              <Button
                variant="destructive"
                size={9}
                onClick={() => setShowDeleteConfirm(true)}
                iconLeft={<TrashIcon size={14} />}
              >
                Delete team
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <p className="text-xs text-fg-secondary">
                {`Permanently delete "${team.name}" and everything in it? This cannot be undone.`}
              </p>
              <Button variant="destructive" size={9} onClick={handleDeleteTeam} isLoading={isDeleting}>
                Delete team
              </Button>
              <Button variant="ghost" size={9} onClick={() => setShowDeleteConfirm(false)}>
                Cancel
              </Button>
            </div>
          )}
        </SettingsSection>
      )}
    </>
  );
}
